<template>
  <div
    ref="containerRef"
    class="fixed right-0 top-2/3 -translate-y-1/2 z-40 "
  >
    <div class="relative h-[236px]">
      <!-- 左侧预览 / 固定展示区域：优先显示 hoverTab，其次 activeTab -->
      <div
        class="flex items-center absolute bottom-0 right-[20px] z-10 w-[240px] bg-[#20AAE8] rounded-l-2xl transition-all duration-300"
        :class="currentTab === 'phone' ? ' h-[77px]' : 'top-0 h-200px]'"
        v-if="currentTab !== null"
        @click.stop
      >
        <!-- 左侧内容区域 -->
        <div>
          <!-- 二维码显示 -->
          <div
            v-if="currentTab !== 'phone'"
            class="p-4"
          >
            <div class="bg-white rounded-xl w-[160px] h-[160px] flex items-center justify-center">
              <img
                :src="getQrCodeImage(currentTab as 'wechat' | 'support')"
                :alt="getTabLabel(currentTab as 'wechat' | 'support')"
                class="w-full h-full object-contain"
              />
            </div>
          </div>
          <!-- 电话号码显示 -->
          <div
            v-else
            class="flex items-end justify-end"
          >
            <div class="bg-[#20AAE8] rounded-l-xl flex items-center justify-center h-[77px] w-[200px] px-6 py-4 ">
              <a
                href="tel:13052585965"
                class="text-white text-2xl font-bold hover:opacity-80 whitespace-nowrap pr-2"
              >
                130 5258 5965
              </a>
            </div>
          </div>
        </div>
      </div>
      <div
        class="bg-[#2961BD] rounded-l-2xl p-2 flex flex-col gap-2 shadow-lg z-20 absolute top-0 right-0 bottom-0"
        @click.stop
      >
        <!-- 企业微信 -->
        <button
          @click="(e) => setActiveTab('wechat', e)"
          @mouseenter="() => setHoverTab('wechat')"
          @mouseleave="clearHoverTab"
          class="w-14 rounded-xl flex flex-col items-center justify-center transition-all duration-200 group relative py-2 px-0"
          :class="activeTab === 'wechat' ? 'bg-[#20AAE8]' : 'bg-transparent hover:bg-[#20AAE8]'"
          :title="'企业微信'"
        >
          <img
            src="@/assets/img/phone_icon.svg"
            alt="合作联系"
            class="w-[34px] h-[34px] object-contain mb-2"
          />
          <span class="text-white text-[10px] text-center leading-tight">企业微信</span>
        </button>

        <!-- 技术支持群 -->
        <button
          @click="(e) => setActiveTab('support', e)"
          @mouseenter="() => setHoverTab('support')"
          @mouseleave="clearHoverTab"
          class="w-14 rounded-xl flex flex-col items-center justify-center transition-all duration-200 group relative py-2 px-0"
          :class="activeTab === 'support' ? 'bg-[#20AAE8]' : 'bg-transparent hover:bg-[#20AAE8]'"
          :title="'技术支持群'"
        >
          <img
            src="@/assets/img/phone_icon.svg"
            alt="合作联系"
            class="w-[34px] h-[34px] object-contain mb-2"
          />
          <span class="text-white text-[10px] text-center leading-tight">技术支持群</span>
        </button>

        <!-- 合作联系 -->
        <button
          @click="(e) => setActiveTab('phone', e)"
          @mouseenter="() => setHoverTab('phone')"
          @mouseleave="clearHoverTab"
          class="w-14 rounded-xl flex flex-col items-center justify-center transition-all duration-200 group relative py-2 px-0"
          :class="activeTab === 'phone' ? 'bg-[#20AAE8]' : 'bg-transparent hover:bg-[#20AAE8]'"
          :title="'合作联系'"
        >
          <img
            src="@/assets/img/qr_icon.svg"
            alt="合作联系"
            class="w-[34px] h-[34px] object-contain mb-1"
          />
          <span class="text-white text-[10px] text-center leading-tight">合作联系</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from "vue";

// TODO: 请将以下占位图片路径替换为实际的二维码图片
// 企业微信二维码 - 请将图片放在 src/assets/img/ 目录下，命名为 qr-wechat.webp
// 技术支持群二维码 - 请将图片放在 src/assets/img/ 目录下，命名为 qr-support.webp
// 然后取消下面的注释并更新路径

import wechatQrCode from "@/assets/img/lx_qr.webp";
import supportQrCode from "@/assets/img/suport_qr.webp";

// 组件容器引用
const containerRef = ref<HTMLElement | null>(null);

// 当前激活的标签（点击固定）
const activeTab = ref<"wechat" | "support" | "phone" | null>(null);

// 当前 hover 的标签（悬停预览）
const hoverTab = ref<"wechat" | "support" | "phone" | null>(null);

// 实际展示用的标签：优先显示 hover，其次 active
const currentTab = computed(() => hoverTab.value ?? activeTab.value);

// 设置激活的标签
const setActiveTab = (
  tab: "wechat" | "support" | "phone",
  event?: MouseEvent
) => {
  // 阻止事件冒泡，避免触发外部点击事件
  if (event) {
    event.stopPropagation();
  }

  if (activeTab.value === tab) {
    // 如果点击的是当前激活的标签，则收起左侧内容
    activeTab.value = null;
  } else {
    activeTab.value = tab;
  }
};

// 设置 hover 预览
const setHoverTab = (tab: "wechat" | "support" | "phone") => {
  hoverTab.value = tab;
};

// 清除 hover 预览
const clearHoverTab = () => {
  hoverTab.value = null;
};

// 获取二维码图片
const getQrCodeImage = (tab: "wechat" | "support") => {
  return tab === "wechat" ? wechatQrCode : supportQrCode;
};

// 获取标签文本
const getTabLabel = (tab: "wechat" | "support" | "phone") => {
  const labels = {
    wechat: "企业微信",
    support: "技术支持群",
    phone: "合作联系",
  };
  return labels[tab];
};

// 点击外部区域关闭
const handleClickOutside = (event: MouseEvent) => {
  if (
    containerRef.value &&
    !containerRef.value.contains(event.target as Node)
  ) {
    activeTab.value = null;
    hoverTab.value = null;
  }
};

// 组件挂载时，添加点击外部监听
onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

// 组件卸载时，移除监听器
onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
});
</script>

<style scoped>
/* 确保组件在所有页面之上，但低于导航栏 */
</style>
